/**
 * SipContext.jsx — Motor de telefonía WebRTC para el sistema de cobranzas
 *
 * CÓMO FUNCIONA EN RESUMEN:
 *   1. El agente conecta su extensión → se llama connect() → JsSIP se registra en Asterisk vía WebSocket seguro (WSS).
 *   2. El agente marca un número → se llama makeCall() → JsSIP envía un INVITE SIP a Asterisk.
 *   3. Asterisk negocia ICE (ruta de red), DTLS (encriptación) y SRTP (audio encriptado).
 *   4. El audio llega al elemento <audio> que vive dentro de este Provider.
 *
 * POR QUÉ USAR CONTEXT (no props):
 *   El teléfono necesita estar activo mientras el agente navega entre módulos.
 *   El SipProvider se pone UNA SOLA VEZ en la raíz del app (App.js) y nunca
 *   se desmonta, así que la conexión SIP sobrevive a cualquier cambio de ruta.
 *   Cualquier componente hijo puede acceder al teléfono con useSip().
 */

import { createContext, useContext, useEffect, useRef, useState } from 'react';
import JsSIP from 'jssip';

// ─── IP del servidor Issabel ────────────────────────────────────────────────
// Se toma de la variable de entorno REACT_APP_SIP_DOMAIN (archivo .env del proyecto).
// El puerto (8089) y la ruta (/ws) son fijos según la configuración de Issabel.
const SIP_DOMAIN = process.env.REACT_APP_SIP_DOMAIN;

const SIP_CONFIG = {
    domain: SIP_DOMAIN,
    websocketUrl: `wss://${SIP_DOMAIN}:8089/ws`,
};
// ────────────────────────────────────────────────────────────────────────────

const SipContext = createContext(null);

const SIP_SESSION_KEY = 'sip_creds';

// ── Íconos SVG inline ────────────────────────────────────────────────────────
const IconError = () => (
    <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
    </svg>
);
const IconWarning = () => (
    <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9.303 3.376c.866 1.5-.217 3.374-1.948 3.374H4.645c-1.73 0-2.813-1.874-1.948-3.374l7.228-12.623c.866-1.5 3.032-1.5 3.898 0l7.228 12.623z" />
    </svg>
);
const IconClose = () => (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
);

const TOAST_STYLES = {
    error: 'bg-red-600 text-white',
    warning: 'bg-yellow-500 text-white',
    info: 'bg-blue-600 text-white',
};

function Toast({ id, type, message, onDismiss }) {
    return (
        <div className={`flex items-start gap-3 px-4 py-3 rounded-lg shadow-xl text-sm max-w-sm w-full pointer-events-auto ${TOAST_STYLES[type] ?? TOAST_STYLES.info}`}>
            {type === 'error' ? <IconError /> : <IconWarning />}
            <span className="flex-1 leading-snug">{message}</span>
            <button
                onClick={() => onDismiss(id)}
                className="opacity-70 hover:opacity-100 transition-opacity mt-0.5"
                aria-label="Cerrar notificación"
            >
                <IconClose />
            </button>
        </div>
    );
}

export function SipProvider({ children }) {

    // ── useRef vs useState ────────────────────────────────────────────────
    // useRef: para objetos que deben persistir entre renders SIN causar un
    //         nuevo render cuando cambian. El UA de JsSIP y la sesión activa
    //         NO deben recrearse en cada render — si estuvieran en useState,
    //         React los destruiría y recrearía cortando la llamada.
    // useState: para valores que SÍ deben actualizar la UI (el punto de color,
    //           el texto de estado, el cronómetro).
    // ─────────────────────────────────────────────────────────────────────

    const uaRef = useRef(null); // JsSIP User Agent (la "central" del lado del navegador)
    const sessionRef = useRef(null); // Llamada activa en curso
    const audioRef = useRef(null); // Elemento <audio> donde suena la voz remota
    const timerRef = useRef(null); // ID del setInterval del cronómetro
    // Evita mostrar "Llamada fallida: Canceled" cuando ya se mostró un error más específico
    const suppressNextFailedToastRef = useRef(false);

    // Estados que actualizan la UI
    const [status, setStatus] = useState('disconnected'); // estado del punto de color
    const [statusText, setStatusText] = useState('Desconectado'); // texto descriptivo del estado
    const [extension, setExtension] = useState(null);           // extensión registrada
    const [phoneAvailable, setPhoneAvailable] = useState(false);
    const [callState, setCallState] = useState('idle');         // 'idle' | 'ringing' | 'active'
    const [callSeconds, setCallSeconds] = useState(0);              // segundos de la llamada activa
    const [callTarget, setCallTarget] = useState(null);
    const [callClientName, setCallClientName] = useState(null);
    const [trackingId, setTrackingId] = useState(null);
    const [dialerRequest, setDialerRequest] = useState(null);
    const [notifications, setNotifications] = useState([]);

    // Auto-reconexión al hacer F5: si existen credenciales en sessionStorage, reconectar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const raw = sessionStorage.getItem(SIP_SESSION_KEY);
        if (!raw) return;
        try {
            connect(JSON.parse(raw));
        } catch {
            sessionStorage.removeItem(SIP_SESSION_KEY);
        }
    }, []);

    // ── Helpers internos ─────────────────────────────────────────────────

    function showToast(type, message) {
        const id = Date.now() + Math.random();
        setNotifications(prev => [...prev, { id, type, message }]);
        const duration = type === 'error' ? 7000 : 5000;
        setTimeout(() => setNotifications(prev => prev.filter(n => n.id !== id)), duration);
    }

    function dismissToast(id) {
        setNotifications(prev => prev.filter(n => n.id !== id));
    }

    function updateStatus(state, text) {
        setStatus(state);
        setStatusText(text);
    }

    function startCallTimer() {
        setCallSeconds(0);
        timerRef.current = setInterval(() => setCallSeconds(s => s + 1), 1000);
    }

    function stopCallTimer() {
        clearInterval(timerRef.current);
        timerRef.current = null;
        setCallSeconds(0);
    }

    // Se llama al terminar o fallar una llamada: limpia todo el estado de llamada.
    function endCallCleanUp() {
        suppressNextFailedToastRef.current = false;
        sessionRef.current = null;
        setCallState('idle');
        setCallTarget(null);
        setCallClientName(null);
        setTrackingId(null);
        stopCallTimer();
        updateStatus('connected', 'Listo para marcar');
    }

    function resetSipState() {
        sessionRef.current = null;
        setCallState('idle');
        setCallTarget(null);
        setCallClientName(null);
        setTrackingId(null);
        stopCallTimer();
        setExtension(null);
        setPhoneAvailable(false);
        updateStatus('disconnected', 'Desconectado');
    }

    // ── Listeners del RTCPeerConnection (capa WebRTC) ────────────────────
    //
    // WebRTC usa tres capas para establecer audio:
    //   ICE   → encuentra la ruta de red entre el navegador y Asterisk
    //   DTLS  → hace el handshake de encriptación (como TLS pero sobre UDP)
    //   SRTP  → envía el audio ya encriptado
    //
    // Esta función adjunta los eventos del RTCPeerConnection para:
    //   - recibir el audio remoto cuando llega el 'track'
    //   - detectar si ICE falla (red no alcanzable)
    //   - loguear el estado de la conexión DTLS

    function setupPeerConnectionListeners(pc) {
        // 'track' se dispara cuando Asterisk envía audio al navegador.
        // Asignamos el stream al elemento <audio> y forzamos reproducción
        // porque el autoplay del navegador puede estar bloqueado.
        pc.addEventListener('track', (e) => {
            const audio = audioRef.current;
            if (!audio) return;
            if (e.streams?.[0]) {
                audio.srcObject = e.streams[0];
            } else if (e.track) {
                // Fallback: algunos navegadores no llenan e.streams, se crea el stream manualmente
                const stream = audio.srcObject instanceof MediaStream
                    ? audio.srcObject
                    : new MediaStream();
                stream.addTrack(e.track);
                audio.srcObject = stream;
            }
            audio.play().catch(() => { });
        });

        // ICE 'failed' significa que el navegador no pudo encontrar
        // ninguna ruta de red hacia Asterisk (problema de firewall/red).
        pc.addEventListener('iceconnectionstatechange', () => {
            if (pc.iceConnectionState === 'failed') {
                showToast('error', 'Error de red (ICE failed). El servidor no es alcanzable para audio.');
                sessionRef.current?.terminate();
                endCallCleanUp();
            }
        });

        // connectionState refleja el estado completo de WebRTC (ICE + DTLS).
        // 'connected' = todo ok, audio fluyendo. 'failed' = error irrecuperable.
        pc.addEventListener('connectionstatechange', () => {
            //console.log('WebRTC conexión:', pc.connectionState);
        });
    }

    // ── Listeners de la sesión SIP ────────────────────────────────────────
    //
    // Cada llamada (entrante o saliente) es un objeto RTCSession de JsSIP.
    // Aquí adjuntamos todos los eventos del ciclo de vida de la llamada.

    function setupSessionListeners(session) {
        // NOTA IMPORTANTE — Bug de orden de eventos en JsSIP 3.x:
        // En llamadas SALIENTES, JsSIP dispara 'peerconnection' ANTES que
        // 'newRTCSession'. Para cuando nuestro código recibe la sesión,
        // el evento ya pasó y nunca lo capturamos.
        // SOLUCIÓN: en newRTCSession (ver más abajo), verificamos si
        // session.connection ya existe y adjuntamos los listeners directamente.
        // Este handler de 'peerconnection' aquí solo aplica a llamadas ENTRANTES,
        // donde el orden es el contrario (newRTCSession primero, peerconnection después).
        session.on('peerconnection', (data) => {
            setupPeerConnectionListeners(data.peerconnection);
        });

        // Si es llamada entrante: mostrar quién llama y responder automáticamente.
        if (session.direction === 'incoming') {
            const caller = session.remote_identity.uri.user;
            setCallTarget(caller);
            setCallClientName(null);
            updateStatus('connecting', `Llamada entrante de ${caller}`);
            setCallState('ringing');
            session.answer({ mediaConstraints: { audio: true, video: false } });
        }

        // No se pudo acceder al micrófono durante la llamada
        session.on('getusermediafailed', (err) => {
            showToast('error', 'No se pudo acceder al micrófono: ' + (err.message || err));
            endCallCleanUp();
        });

        // 'progress' = Asterisk está procesando (100 Trying) o sonando (180 Ringing)
        session.on('progress', (e) => {
            const code = e.response?.status_code ?? '';
            updateStatus('connecting', `Timbrado... ${code}`);
            setCallState('ringing');
        });

        // Error al aplicar el SDP de Asterisk. Casi siempre es el certificado
        // DTLS no configurado en Issabel (dtlscertfile vacío o archivo inexistente).
        // Marcamos el flag para que el evento 'failed' subsecuente (causa: Canceled)
        // no muestre un segundo toast redundante.
        session.on('peerconnection:setremotedescriptionfailed', () => {
            suppressNextFailedToastRef.current = true;
            showToast('error', 'Error DTLS en Asterisk. Verifica que el certificado dtls.pem esté generado y configurado en Issabel.');
            sessionRef.current?.terminate();
            endCallCleanUp();
        });

        // 'accepted' = el destinatario contestó (200 OK de SIP recibido)
        session.on('accepted', () => {
            updateStatus('connected', 'En llamada activa');
            setCallState('active');
            startCallTimer();
        });

        // 'failed' = la llamada no pudo conectarse por alguna razón
        // Los causes más comunes: Rejected (ocupado), No Answer, Not Found, Unavailable
        session.on('failed', (e) => {
            if (suppressNextFailedToastRef.current) {
                suppressNextFailedToastRef.current = false;
                endCallCleanUp();
                return;
            }
            const cause = e.cause || 'desconocida';
            const code = e.response ? ` (${e.response.status_code} ${e.response.reason_phrase})` : '';
            showToast('warning', `Llamada fallida: ${cause}${code}`);
            endCallCleanUp();
        });

        // 'ended' = llamada terminada normalmente (alguien colgó)
        session.on('ended', () => endCallCleanUp());
    }

    // ── API pública ──────────────────────────────────────────────────────

    /**
     * connect({ username, extension, password })
     * Registra la extensión SIP en Asterisk.
     *
     * Flujo interno:
     *   1. Crea un WebSocket seguro (WSS) hacia Asterisk puerto 8089
     *   2. JsSIP envía REGISTER → Asterisk responde 401 → JsSIP reenvía con hash MD5
     *   3. Asterisk responde 200 OK → el agente queda registrado
     */
    async function connect({ username, extension: ext, password }) {
        if (uaRef.current) {
            uaRef.current.stop();
            uaRef.current = null;
        }

        resetSipState();
        setPhoneAvailable(true);

        JsSIP.debug.enable('JsSIP:*'); // habilita logs en consola del navegador (útil para debug)

        const socket = new JsSIP.WebSocketInterface(SIP_CONFIG.websocketUrl);
        const ua = new JsSIP.UA({
            sockets: [socket],
            uri: `sip:${ext}@${SIP_CONFIG.domain}`,
            password,
            authorization_user: username,
            register: true,
        });

        ua.on('connecting', () => updateStatus('connecting', 'Conectando...'));
        ua.on('connected', () => updateStatus('connecting', 'Registrando SIP...'));
        ua.on('disconnected', () => {
            if (uaRef.current !== ua) return;
            updateStatus('disconnected', 'Desconectado');
            setExtension(null);
            setPhoneAvailable(false);
            uaRef.current = null;
        });
        ua.on('registered', () => {
            if (uaRef.current !== ua) return;
            updateStatus('connected', `Conectado como ${ext}`);
            setExtension(ext);
        });
        ua.on('registrationFailed', (e) => {
            if (uaRef.current !== ua) return;
            updateStatus('disconnected', 'Registro fallido');
            setPhoneAvailable(false);
            showToast('error', 'Error de Registro SIP: ' + e.cause);
            ua.stop();
            uaRef.current = null;
        });

        // newRTCSession dispara para TODA llamada (entrante y saliente).
        ua.on('newRTCSession', (data) => {
            const session = data.session;
            sessionRef.current = session;
            setupSessionListeners(session);

            // FIX del bug de JsSIP 3.x:
            // En llamadas salientes, 'peerconnection' ya se disparó antes de llegar aquí.
            // session.connection ya tiene el RTCPeerConnection creado, así que
            // adjuntamos los listeners directamente sin esperar el evento.
            if (session.connection) {
                setupPeerConnectionListeners(session.connection);
            }
        });

        uaRef.current = ua;
        sessionStorage.setItem(SIP_SESSION_KEY, JSON.stringify({ username, extension: ext, password }));
        ua.start();
    }

    /**
     * disconnect()
     * Cierra el registro SIP.
     */
    function disconnect() {
        const ua = uaRef.current;
        uaRef.current = null;
        sessionRef.current?.terminate();
        ua?.stop();
        sessionStorage.removeItem(SIP_SESSION_KEY);
        resetSipState();
    }

    /**
     * makeCall(target)
     * Inicia una llamada al número o extensión indicada.
     * Verifica acceso al micrófono antes de marcar.
     *
     * @param {string} target  Número o extensión a llamar (ej: "0991234567" o "101")
     */
    function openDialer(options = {}) {
        setDialerRequest({
            requestId: Date.now(),
            trackingId: options.trackingId ?? null,
            clientName: options.clientName ?? null,
            target: options.target ?? null,
        });
    }

    async function makeCall(target, trackingIdExterno = null, callMeta = {}) {
        if (!uaRef.current?.isRegistered()) {
            showToast('error', 'El teléfono no está registrado. Reconecta la extensión.');
            return;
        }

        // El marcado interno antepone 9 al número completo (0999033751 -> 90999033751)
        const visibleTarget = String(target || '').trim();
        target = visibleTarget;

        if (!visibleTarget) {
            showToast('warning', 'No hay un número disponible para llamar.');
            return;
        }

        if (/^0\d{9}$/.test(target)) {
            target = `9${target}`;
        }

        // Verificar micrófono antes de marcar para dar mensaje claro al usuario.
        // Se obtiene y se suelta inmediatamente — JsSIP lo pedirá de nuevo al hacer la llamada.
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
            stream.getTracks().forEach(t => t.stop());
        } catch (err) {
            const msgs = {
                NotAllowedError: 'Permiso de micrófono denegado. Habilítalo en la configuración del navegador.',
                NotFoundError: 'No se encontró ningún micrófono en este dispositivo.',
            };
            showToast('error', msgs[err.name] || 'No se pudo acceder al micrófono.');
            return;
        }

        setCallTarget(visibleTarget);
        setCallClientName(callMeta.clientName || null);
        updateStatus('connecting', `Llamando a ${visibleTarget}...`);
        setCallState('ringing');

        try {
            const trackingIdLlamada = trackingIdExterno ?? crypto.randomUUID();
            setTrackingId(trackingIdLlamada);

            const session = uaRef.current.call(`sip:${target}@${SIP_CONFIG.domain}`, {
                mediaConstraints: { audio: true, video: false },

                extraHeaders: [

                    `X-Tracking-ID: ${trackingIdLlamada}`
                ],

                pcConfig: { iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] },

                rtcOfferConstraints: { offerToReceiveAudio: true, offerToReceiveVideo: false },
            });

            session.trackingId = trackingIdLlamada;

            //console.log('Tracking ID enviado:', trackingIdLlamada);
        } catch (err) {
            showToast('error', 'Error al iniciar llamada: ' + err.message);
            endCallCleanUp();
        }
    }

    /**
     * hangup()
     * Cuelga la llamada activa.
     */
    function hangup() {
        sessionRef.current?.terminate();
    }

    // ── Render ────────────────────────────────────────────────────────────
    // El elemento <audio> vive aquí (en el Provider) para que nunca se desmonte.
    // Si estuviera en PhoneWidget o en cualquier componente de ruta, se destruiría
    // al navegar y cortaría el audio a mitad de llamada.

    return (
        <SipContext.Provider value={{
            status, statusText, extension, phoneAvailable,
            callState, callSeconds, callTarget, callClientName, trackingId, dialerRequest,
            connect, disconnect, makeCall, openDialer, hangup,
        }}>
            {children}
            <audio ref={audioRef} autoPlay />

            {/* ── Toast de notificaciones SIP ────────────────────────────── */}
            <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none">
                {notifications.map(n => (
                    <Toast
                        key={n.id}
                        id={n.id}
                        type={n.type}
                        message={n.message}
                        onDismiss={dismissToast}
                    />
                ))}
            </div>
        </SipContext.Provider>
    );
}

/**
 * useSip()
 * Hook para acceder al teléfono desde cualquier componente.
 *
 * Uso:
 *   const { makeCall, status } = useSip();
 *
 * Lanza error si se usa fuera de <SipProvider> (para detectar errores de integración).
 */
export function useSip() {
    const ctx = useContext(SipContext);
    if (!ctx) throw new Error('useSip debe usarse dentro de <SipProvider>');
    return ctx;
}
